/**
 * Cloud projects.
 *
 * The editor already speaks `.tui.json`; this dialog only moves that exact
 * text to and from the account. Opening a cloud project goes through the same
 * validated `loadProject` path as a local file, so a hostile project gets the
 * same treatment from the server as from the disk.
 */

import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  Cloud,
  CloudUpload,
  FolderOpen,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useEditor } from '../state/store';
import { Modal } from '../ui/widgets/common';
import { ApiError, ProjectMeta, Usage, api, formatBytes, formatDate } from './api';

type Mode = 'save' | 'open';

export function CloudDialog({ mode, onClose }: { mode: Mode; onClose: () => void }): JSX.Element {
  const [projects, setProjects] = useState<ProjectMeta[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState(() => {
    const doc = useEditor.getState().active().doc;
    return doc.name.trim() || 'untitled';
  });
  const [armed, setArmed] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{ projects: ProjectMeta[]; usage: Usage }>('/api/projects');
      setProjects(result.projects);
      setUsage(result.usage);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const store = useEditor.getState();
      const project = await api<{ project: ProjectMeta; usage: Usage }>('/api/projects', {
        method: 'POST',
        body: {
          name: name.trim() || 'untitled',
          data: store.projectJson(true),
        },
      });
      store.notify(`Saved ${project.project.name} to your account.`, 'success');
      setNotice(`Saved “${project.project.name}” · ${formatBytes(project.project.bytes)}`);
      setUsage(project.usage);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the project.');
    } finally {
      setBusy(false);
    }
  };

  const open = async (project: ProjectMeta) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ project: ProjectMeta; data: string }>(`/api/projects/${project.id}`);
      const store = useEditor.getState();
      store.loadProject(result.data, `${project.name}.tui.json`);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not open the project.');
      setBusy(false);
    }
  };

  const remove = async (project: ProjectMeta) => {
    if (armed !== project.id) {
      setArmed(project.id);
      window.setTimeout(() => setArmed((current) => (current === project.id ? null : current)), 4000);
      return;
    }
    setArmed(null);
    setBusy(true);
    try {
      const result = await api<{ ok: boolean; usage: Usage }>(`/api/projects/${project.id}`, {
        method: 'DELETE',
      });
      setUsage(result.usage);
      await load();
      setNotice(`Deleted “${project.name}”.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not delete the project.');
    } finally {
      setBusy(false);
    }
  };

  const percent = usage && usage.quotaBytes > 0 ? Math.min(100, (usage.bytes / usage.quotaBytes) * 100) : 0;

  return (
    <Modal
      title={mode === 'save' ? 'Save to your account' : 'Cloud projects'}
      onClose={onClose}
      width={760}
      description={mode === 'save' ? 'Save the current document to your account' : 'Open a project from your account'}
    >
      <div className="cloud-head">
        <div className="cloud-usage">
          <div className="cloud-usage-label">
            <Cloud size={14} />
            <span>
              {usage ? `${usage.count} / ${usage.maxProjects} projects` : 'Loading usage…'}
            </span>
            <span className="spacer" />
            <span className="dim">
              {usage ? `${formatBytes(usage.bytes)} of ${formatBytes(usage.quotaBytes)}` : ''}
            </span>
          </div>
          <div className="cloud-bar">
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>
        <button className="btn icon" onClick={() => void load()} title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {mode === 'save' ? (
        <form className="cloud-save" onSubmit={save}>
          <label className="field">
            <span>Project name</span>
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              required
            />
          </label>
          <p className="hint-text">
            Saves the whole document — layers, variables, components and animations — as a cloud project.
          </p>
          <div className="row">
            <span className="spacer" />
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? <Loader2 size={14} className="spin" /> : <CloudUpload size={14} />} Save to account
            </button>
          </div>
        </form>
      ) : null}

      {error ? (
        <p className="form-note error">
          <AlertCircle size={14} /> {error}
        </p>
      ) : null}
      {notice ? (
        <p className="form-note success">
          <Check size={14} /> {notice}
        </p>
      ) : null}

      <div className="cloud-list">
        {loading ? (
          <p className="cloud-empty">
            <Loader2 size={15} className="spin" /> Loading projects…
          </p>
        ) : projects.length === 0 ? (
          <p className="cloud-empty">
            <Cloud size={18} /> No cloud projects yet. Use “Save to your account” to make one.
          </p>
        ) : (
          projects.map((project) => (
            <div className="cloud-row" key={project.id}>
              <div className="cloud-icon">
                <FolderOpen size={16} />
              </div>
              <div className="cloud-meta">
                <strong>{project.name}</strong>
                <span>
                  {formatBytes(project.bytes)} · updated {formatDate(project.updatedAt)}
                </span>
              </div>
              <div className="row">
                <button className="btn small" onClick={() => void open(project)} disabled={busy}>
                  <FolderOpen size={13} /> Open
                </button>
                <button
                  className={`btn small danger${armed === project.id ? ' solid' : ''}`}
                  onClick={() => void remove(project)}
                  disabled={busy}
                  title={armed === project.id ? 'Click again to delete' : 'Delete project'}
                >
                  <Trash2 size={13} />
                  {armed === project.id ? 'Confirm' : ''}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
